import React from "react";
import "./index.scss";

import { createPortal } from "react-dom";
import { ReactNode, cloneElement, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { getRandomId } from "@/utils";

export const debounce = (func: any, wait: number) => {
  let timeout: number;

  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait) as unknown as number;
  };
};

// children 中可能包裹了其他组件，层级也可能比较深
// 该方法用于从 children 中从外向内查找第一个 HTMLElement
// 注意：仅查找 children 的 jsx 结构，不会查找组件内部的 jsx 结构

export enum Trigger {
  click = "click",
  hover = "hover",
}

export enum Placement {
  bottomCenter = "bottom-center",
  bottomLeft = "bottom-left",
  bottomRight = "bottom-right",
  topCenter = "top-center",
  topLeft = "top-left",
  topRight = "top-right",
}

export type DropdownProps = {
  children: JSX.Element;
  popContent: JSX.Element;
  trigger?: Trigger;
  placement?: Placement;
  className?: string;
  zIndex?: number;
  onOpenChange?: (open: boolean) => void;

  // 非常规用法
  customTriggerDom?: HTMLElement;
  mountDom?: {
    show: () => void;
    hide: () => void;
  } & HTMLElement;
  syncWidth?: boolean;
};

const Dropdown = (props: DropdownProps) => {
  const {
    children,
    popContent,
    placement = Placement.bottomLeft,
    className,
    zIndex = 2000,
    onOpenChange,

    customTriggerDom,
    mountDom,
    trigger=Trigger.click,
    syncWidth = false,
  } = props;

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dropdownLeft, setDropdownLeft] = useState(0);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [dropdownWidth, setDropdownWidth] = useState<number | "unset">("unset");
  const [uniqueTriggerClassName] = useState(`t__${getRandomId()}`);
  const triggerRef = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  const isBottom = [
    Placement.bottomLeft,
    Placement.bottomRight,
    Placement.bottomCenter,
  ].includes(placement);

  const clickTrigger = () => {
    setOpen((value) => {
      const res = !value;
      onOpenChange && onOpenChange(res);
      return res;
    });
  };

  const getPosToDoc = (element: HTMLElement) => {
    return {
      left: element.getBoundingClientRect().left + window.scrollX,
      top: element.getBoundingClientRect().top + window.scrollY,
    };
  };

  const clickOutside = (e: any) => {
    const triggerDom = triggerRef.current || customTriggerDom;
    if (
      !triggerDom.contains(e.target) &&
      !dropdownRef.current.contains(e.target)
    ) {
      onOpenChange && onOpenChange(false);
      setOpen(false);
    }
  };

  const calcDropdownPosition = () => {
    const triggerDom = triggerRef.current || customTriggerDom;
    if (!triggerDom) return;
    if (!dropdownRef.current) return;
    const triggerPosToDoc = getPosToDoc(triggerDom);
    const triggerWidth = triggerDom.offsetWidth;
    const triggerHeight = triggerDom.offsetHeight;
    const dropdownWidth = dropdownRef.current.offsetWidth;
    const dropdownHeight = dropdownRef.current.offsetHeight;
    if ([Placement.bottomLeft, Placement.topLeft].includes(placement)) {
      const maxLeft = window.innerWidth - dropdownWidth - 20;
      setDropdownLeft(Math.min(triggerPosToDoc.left, maxLeft));
    } else if (
      [Placement.bottomRight, Placement.topRight].includes(placement)
    ) {
      setDropdownLeft(triggerPosToDoc.left - (dropdownWidth - triggerWidth));
    } else {
      setDropdownLeft(
        triggerPosToDoc.left - (dropdownWidth - triggerWidth) / 2
      );
    }
    if (isBottom) {
      const rect = triggerDom.getBoundingClientRect(); // 获取元素相对于视口的位置
      const nowBottom = rect.bottom + 10 + dropdownHeight;
      if (nowBottom > window.innerHeight) {
        setDropdownTop(triggerPosToDoc.top - dropdownHeight - 20);
      } else {
        setDropdownTop(triggerHeight + triggerPosToDoc.top);
      }
    } else {
      setDropdownTop(triggerPosToDoc.top - dropdownHeight);
    }
  };

  const calcDropdownPosition_debounce = debounce(calcDropdownPosition, 200);

  // 初始化 trigger ref
  useEffect(() => {
    if (children) {
      triggerRef.current = document.querySelector(`.${uniqueTriggerClassName}`);
    }
  }, []);

  // 监听 trigger 点击事件
  useEffect(() => {
    if (children) {
      if (trigger === Trigger.click) {
        triggerRef.current.addEventListener("click", clickTrigger);
        return () => {
          triggerRef.current.removeEventListener("click", clickTrigger);
        };
      } else if (trigger === Trigger.hover) {
        triggerRef.current.addEventListener("mouseenter", clickTrigger);
        triggerRef.current.addEventListener("mouseleave", clickTrigger);
        return () => {
          triggerRef.current.removeEventListener("mouseenter", clickTrigger);
          triggerRef.current.removeEventListener("mouseleave", clickTrigger);
        };
      }
    }
  }, []);

  // 监听 click outside 事件
  useEffect(() => {
    if (children) {
      window.addEventListener("click", clickOutside, true); // 在捕获阶段执行，避免被阻止冒泡

      return () => {
        window.removeEventListener("click", clickOutside, true);
      };
    }
  }, []);

  // 计算 dropdown 位置
  useEffect(() => {
    if (open) {
      calcDropdownPosition();
    }
  }, [open, placement]);

  // 监听 resize 事件，计算 dropdown 位置
  useEffect(() => {
    window.addEventListener("resize", calcDropdownPosition_debounce);

    return () => {
      window.removeEventListener("resize", calcDropdownPosition_debounce);
    };
  }, [placement]);

  // 同步 visible 和 open 状态
  useEffect(() => {
    setVisible(open);
  }, [open]);

  // not-react 环境下，注册开启关闭方法
  // useEffect(() => {
  //   if (mountDom) {
  //     mountDom.show = () => {
  //       if (syncWidth && customTriggerDom) {
  //         setDropdownWidth(customTriggerDom.offsetWidth);
  //       }
  //       onOpenChange && onOpenChange(true);

  //       setOpen(true);
  //     };

  //     mountDom.hide = () => {
  //       onOpenChange && onOpenChange(false);

  //       setOpen(false);
  //     };
  //   }
  // }, []);

  return (
    <>
      {React.Children.map(props.children, (child) => {
        return React.cloneElement(child, {
          ...child.props,
          ...mountDom,
          className: clsx(
            child.props.className,
            uniqueTriggerClassName,
            "dn-dropdown-trigger"
          ),
        });
      })}
      {createPortal(
        <div
          className={clsx(
            "dn-dropdown",
            className,
            !open && "dn-dropdown-none",
            !visible && "dn-dropdown-hidden"
          )}
          style={{
            left: `${dropdownLeft}px`,
            top: `${dropdownTop}px`,
            width:
              typeof dropdownWidth === "number"
                ? `${dropdownWidth}px`
                : dropdownWidth,
            transform: `translateY(${isBottom ? 10 : -10}px)`,
            zIndex,
          }}
          ref={dropdownRef}
          onClick={(e) => {
            if (trigger === Trigger.click) {
              clickTrigger();
            }
          }}
        >
          {popContent}
        </div>,
        document.body
      )}
    </>
  );
};

export default Dropdown;
